/**
 * The quote form — three fields (§13.7): amount, days, note.
 *
 * Not four. Not a spec-acknowledgement checkbox, not a GST toggle, not a
 * validity-period picker. The vendor is standing at a press with one hand on
 * the phone, and every field added here is a quote that does not get sent.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, type Invitation, type JobsResponse } from '../lib/api';
import { useI18n, capabilityLabel } from '../lib/i18n';
import { formatINR, formatDue, formatClosesIn } from '../lib/format';
import {
  ActionBar,
  Card,
  ErrorNote,
  Field,
  Spinner,
  SubHeader,
} from '../components/Primitives';
import { Icon } from '../components/Icon';

export function QuotePage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { t, lang } = useI18n();

  const [job, setJob] = useState<Invitation | null>(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState('');
  const [days, setDays] = useState('2');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [won, setWon] = useState(false);

  const load = useCallback(async () => {
    try {
      const jobs = await api.get<JobsResponse>('/v1/partner/jobs');
      setJob(jobs.invitations.find((i) => i.jobId === jobId) ?? null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.somethingWrong'));
    } finally {
      setLoading(false);
    }
  }, [jobId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ awarded: boolean }>(`/v1/partner/jobs/${jobId}/quote`, {
        amount: amount.replace(/[^\d.]/g, ''),
        leadTimeDays: Number(days),
        ...(note.trim() ? { note: note.trim() } : {}),
      });

      if (res.awarded) {
        setWon(true);
        setBusy(false);
        return;
      }
      navigate('/jobs', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.somethingWrong'));
      setBusy(false);
    }
  }

  async function decline() {
    setBusy(true);
    try {
      await api.post(`/v1/partner/jobs/${jobId}/decline`);
      navigate('/jobs', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.somethingWrong'));
      setBusy(false);
    }
  }

  if (loading) return <Spinner />;
  if (!job)
    return <ErrorNote message={t('common.somethingWrong')} onRetry={() => navigate('/jobs')} />;

  if (won) {
    return (
      <div className="py-10 text-center">
        <span
          aria-hidden
          className="mx-auto flex h-20 w-20 items-center justify-center rounded-[28px] text-white shadow-lift"
          style={{ backgroundImage: 'linear-gradient(135deg, var(--sec-from), var(--sec-to))' }}
        >
          <Icon name="check" className="h-10 w-10" strokeWidth={2.4} />
        </span>
        <h1 className="mt-5 font-display text-2xl font-bold">{t('quote.won')}</h1>
        <button type="button" className="btn-primary mt-8" onClick={() => navigate('/jobs')}>
          {t('nav.jobs')}
        </button>
      </div>
    );
  }

  const closesIn = formatClosesIn(job.closesAt, lang);

  return (
    <div>
      <SubHeader
        back="/jobs"
        icon="printer"
        title={job.title}
        sub={`${capabilityLabel(job.capability, lang)} · ${job.city} · ${formatDue(job.neededBy, lang)}${
          closesIn ? ` · ${closesIn} ${t('jobs.closesIn')}` : ''
        }`}
      />

      {/* The spec, rendered plainly. The vendor is pricing off these numbers,
          so they go above the form rather than behind a "details" tap. */}
      {job.spec && Object.keys(job.spec).length > 0 && (
        <Card className="mt-4">
          <p className="field-label">{t('jobs.spec')}</p>
          <dl className="space-y-1 text-sm">
            {Object.entries(job.spec).map(([key, value]) => (
              <div key={key} className="flex justify-between gap-3">
                <dt className="text-muted">{key.replace(/_/g, ' ')}</dt>
                <dd className="tnum text-right font-medium">{String(value)}</dd>
              </div>
            ))}
          </dl>
        </Card>
      )}

      <Card className="mt-4">
        <p className="field-label">{t('jobs.proofNeeded')}</p>
        <p className="text-sm text-muted">{job.proofRequired}</p>
      </Card>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        className="mt-4 space-y-4"
      >
        <Card className="space-y-4">
          <Field label={t('quote.amount')} hint={t('quote.amountHint')}>
            <input
              type="text"
              inputMode="decimal"
              autoFocus
              placeholder="8500"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
              className="tnum text-2xl font-semibold"
            />
          </Field>

          <Field label={t('quote.days')}>
            <select value={days} onChange={(e) => setDays(e.target.value)} className="tnum">
              {[0, 1, 2, 3, 4, 5, 6, 7, 10, 14, 21, 30].map((d) => (
                <option key={d} value={String(d)}>
                  {d === 0
                    ? lang === 'hi'
                      ? 'आज ही'
                      : 'Same day'
                    : `${d} ${t('common.days')}`}
                </option>
              ))}
            </select>
          </Field>

          <Field label={`${t('quote.note')} (${t('common.optional')})`}>
            <textarea
              rows={2}
              placeholder={t('quote.notePlaceholder')}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="py-3"
            />
          </Field>
        </Card>

        {amount && (
          <p className="text-center text-sm text-muted">
            <span className="tnum font-display text-lg font-semibold text-text">
              {formatINR(BigInt(Math.round(Number(amount) * 1_000_000)))}
            </span>
          </p>
        )}

        {error && <p className="text-sm text-bad">{error}</p>}

        <ActionBar>
          <button type="submit" className="btn-primary" disabled={busy || !amount}>
            {busy ? t('quote.sending') : t('quote.submit')}
          </button>
          <button
            type="button"
            className="btn-quiet mt-3 block w-full pb-1 text-center text-sm"
            onClick={() => void decline()}
            disabled={busy}
          >
            {t('jobs.decline')}
          </button>
        </ActionBar>
      </form>
    </div>
  );
}
